<template>
  <div class="problem-list">
    <div class="toolbar">
      <el-input
        v-model="searchTitle"
        placeholder="按标题搜索"
        :prefix-icon="Search"
        clearable
        class="search-input"
        @keyup.enter="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button v-if="isAdmin" type="success" @click="router.push('/problem/create')">创建题目</el-button>
    </div>

    <el-skeleton :loading="loading" animated :count="6">
      <template v-if="!loading">
        <el-table
          v-if="problemList.length > 0"
          :data="problemList"
          stripe
          @row-click="goToProblem"
          class="problem-table"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column label="标题" min-width="260">
            <template #default="{ row }">
              <span class="problem-title">{{ row.title }}</span>
            </template>
          </el-table-column>
          <el-table-column label="标签" min-width="200">
            <template #default="{ row }">
              <el-tag
                v-for="tag in row.tags"
                :key="tag"
                size="small"
                class="tag"
                type="primary"
                effect="light"
              >
                {{ tag }}
              </el-tag>
              <span v-if="!row.tags || row.tags.length === 0" class="no-tags">-</span>
            </template>
          </el-table-column>
          <el-table-column label="通过率" width="120" align="center">
            <template #default="{ row }">
              <span v-if="row.submitNum === 0">-</span>
              <span v-else :class="passRateClass(row)">{{ passRate(row) }}%</span>
            </template>
          </el-table-column>
          <el-table-column prop="acceptedNum" label="通过" width="80" align="center" />
          <el-table-column prop="submitNum" label="提交" width="80" align="center" />
        </el-table>

        <el-empty v-else description="暂无题目" />

        <el-pagination
          v-if="total > pageSize"
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next, total"
          background
          class="pagination"
          @current-change="fetchProblems"
        />
      </template>
    </el-skeleton>

    <el-alert
      v-if="error"
      type="error"
      :title="error"
      closable
      class="error-alert"
      @close="error = ''"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { listQuestionByPage } from '@/api/timuguanli'
import { useLoginUserStore } from '@/store/loginUser'
import ACCESS_ENUM from '@/access/accessEnum'

const router = useRouter()
const loginUserStore = useLoginUserStore()
const isAdmin = computed(() => loginUserStore.loginUser.userRole === ACCESS_ENUM.ADMIN)

const problemList = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(15)
const searchTitle = ref('')
const loading = ref(false)
const error = ref('')

function passRate(row) {
  if (row.submitNum === 0) return 0
  return ((row.acceptedNum / row.submitNum) * 100).toFixed(1)
}

function passRateClass(row) {
  const rate = row.acceptedNum / row.submitNum
  if (rate >= 0.7) return 'rate-high'
  if (rate >= 0.3) return 'rate-mid'
  return 'rate-low'
}

async function fetchProblems() {
  loading.value = true
  error.value = ''
  try {
    const res = await listQuestionByPage({
      current: currentPage.value,
      pageSize: pageSize.value,
      title: searchTitle.value || undefined,
      sortField: 'createTime',
      sortOrder: 'descend',
    })
    if (res.code === 0) {
      problemList.value = res.data?.records ?? []
      total.value = res.data?.total ?? 0
    } else {
      throw new Error(res.message || '获取题目列表失败')
    }
  } catch (e) {
    error.value = e.message || '网络错误'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  currentPage.value = 1
  fetchProblems()
}

function goToProblem(row) {
  router.push(`/problem/${row.id}`)
}

onMounted(fetchProblems)
</script>

<style scoped>
.problem-list {
  padding: 28px;
  border-radius: 24px;
  border: 1px solid #d6d3d1;
  background: #ffffff;
  box-shadow: 0 18px 36px -18px rgba(120, 113, 108, 0.42), 0 5px 14px -8px rgba(120, 113, 108, 0.32);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #d6d3d1;
}

.search-input {
  width: min(340px, 100%);
  margin-right: auto;
}

.problem-table {
  width: 100%;
  cursor: pointer;
}

.problem-title {
  color: var(--yoj-accent-strong);
  font-weight: 600;
  transition: color 200ms ease-in-out;
}

.problem-title:hover {
  color: #9a3412;
}

.tag {
  margin: 3px 8px 3px 0;
}

.no-tags {
  color: var(--yoj-text-muted);
}

.rate-high,
.rate-mid,
.rate-low {
  font-family: Consolas, monospace;
  font-weight: 600;
}

.rate-high {
  color: #047857;
}

.rate-mid {
  color: #b45309;
}

.rate-low {
  color: #be123c;
}

.pagination {
  justify-content: center;
  margin-top: 28px;
}

.error-alert {
  margin-top: 20px;
}

@media (max-width: 640px) {
  .problem-list {
    padding: 20px 16px;
  }

  .search-input {
    width: 100%;
  }

  .toolbar .el-button {
    flex: 1;
    margin-left: 0;
  }
}
</style>
